import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { OrderSummary, BookingDetailModal, isOrderMissing } from "@/components/BookingDetail";
import { toast } from "sonner";
import {
  Calendar, Users, LogOut, Trash2, Plus, CheckCircle2,
  XCircle, Clock, Search, ScanLine, QrCode, ChevronLeft, ChevronRight, X, Printer,
} from "lucide-react";

const STATUSES = ["pending", "confirmed", "checked_in", "cancelled"];

const STATUSES_DISPLAY = {
  pending: { c: "text-yellow-400 border-yellow-400/40", Icon: Clock },
  pending_payment: { c: "text-orange-400 border-orange-400/40", Icon: Clock },
  confirmed: { c: "text-neon border-neon/40", Icon: CheckCircle2 },
  checked_in: { c: "text-green-400 border-green-400/40", Icon: CheckCircle2 },
  cancelled: { c: "text-red-400 border-red-400/40", Icon: XCircle },
};

/**
 * Staff dashboard — full edit power.
 * Taylor & door staff handle: door check-ins, bookings, events CRUD, media, 6+6 cards.
 */
export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("tonight");

  return (
    <div data-testid="staff-dashboard" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-black/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-10 pt-3 pb-2 md:py-4">
          {/* Row 1 — brand + logout (mobile stacks this above tabs) */}
          <div className="flex items-center justify-between gap-3">
            <Link to="/" className="font-heading tracking-widest shrink-0 flex items-baseline gap-2 min-w-0">
              <span className="text-white text-xl sm:text-2xl">SOHO</span>
              <span className="text-neon text-xl sm:text-2xl">69</span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.32em] text-neon/80 ml-1 truncate">
                Taylor&apos;s Command Centre
              </span>
            </Link>
            <div className="flex items-center gap-3 shrink-0">
              <span className="hidden md:block text-xs uppercase tracking-widest text-white/60">
                {user?.name} · {user?.role}
              </span>
              <button onClick={logout} data-testid="staff-logout"
                      className="text-[11px] uppercase tracking-widest text-white/70 hover:text-neon flex items-center gap-1.5 border border-white/15 rounded-full px-3 py-1.5">
                <LogOut size={14} /> <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
          {/* Row 2 — tabs (horizontally scrollable on mobile) */}
          <nav
            data-testid="staff-tab-nav"
            className="mt-2 md:mt-3 -mx-3 sm:-mx-4 md:mx-0 px-3 sm:px-4 md:px-0 flex gap-1 overflow-x-auto no-scrollbar snap-x"
          >
            <TopTab active={tab === "tonight"} onClick={() => setTab("tonight")} icon={ScanLine} testid="staff-tab-tonight">Tonight</TopTab>
            <TopTab active={tab === "bookings"} onClick={() => setTab("bookings")} icon={Users} testid="staff-tab-bookings">Bookings</TopTab>
            <TopTab active={tab === "events"} onClick={() => setTab("events")} icon={Calendar} testid="staff-tab-events">Events</TopTab>
            <TopTab active={tab === "loyalty"} onClick={() => setTab("loyalty")} icon={QrCode} testid="staff-tab-loyalty">6+6 Cards</TopTab>
          </nav>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-10 py-5 sm:py-7 md:py-10 pb-24 md:pb-10">
        {tab === "tonight" && <TonightTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "events" && <EventsTab />}
        {tab === "loyalty" && <LoyaltyTab />}
      </main>
    </div>
  );
}

/* ───────────────────────────── TONIGHT ───────────────────────────── */
function TonightTab() {
  const [bookings, setBookings] = useState([]);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));
  const [stats, setStats] = useState(null);
  const [monthCounts, setMonthCounts] = useState({}); // { "2026-07-15": 3, ... }
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  // Settlement ledger — Taylor's payouts to the Club. Reduces the Current
  // Balance tile, never touches Lifetime Revenue.
  const [settlements, setSettlements] = useState([]);
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [settleAmount, setSettleAmount] = useState("");
  const [settleNote, setSettleNote] = useState("");
  const [settleSaving, setSettleSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, s, se] = await Promise.all([
        api.get("/bookings", { params: dateFilter ? { date: dateFilter } : {} }),
        api.get("/admin/stats"),
        api.get("/admin/settlements"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setSettlements(se.data?.settlements || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [dateFilter]);

  // Fetch every booking in the visible month once — used to paint dots on
  // days that actually have bookings.
  const loadMonthCounts = useCallback(async () => {
    try {
      const from = monthAnchor.toISOString().slice(0, 10);
      const to = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0)
        .toISOString().slice(0, 10);
      const r = await api.get("/bookings", { params: { date_from: from, date_to: to } });
      const counts = {};
      for (const b of r.data || []) {
        if (!b.date) continue;
        counts[b.date] = (counts[b.date] || 0) + 1;
      }
      setMonthCounts(counts);
    } catch {
      // Non-fatal — dots simply won't render.
      setMonthCounts({});
    }
  }, [monthAnchor]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMonthCounts(); }, [loadMonthCounts]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitSettlement = async () => {
    const amount = parseFloat(settleAmount);
    if (!amount || amount <= 0) {
      toast.error("Amount must be greater than €0");
      return;
    }
    setSettleSaving(true);
    try {
      await api.post("/admin/settlements", {
        amount_eur: amount,
        note: settleNote,
      });
      toast.success(`Recorded €${amount} settlement`);
      setSettleModalOpen(false);
      setSettleAmount("");
      setSettleNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettleSaving(false);
    }
  };

  const undoSettlement = async (id) => {
    try {
      await api.delete(`/admin/settlements/${id}`);
      toast.success("Settlement removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = bookings.filter((b) => {
    if (!q) return true;
    const t = `${b.name} ${b.email} ${b.whatsapp} ${b.table_name || ""} ${b.event_title || ""}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  return (
    <div data-testid="staff-tonight-tab">
      <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight uppercase leading-tight">
        Taylor&apos;s <span className="italic font-display text-neon">Command Centre</span>
      </h1>
      <p className="text-white/60 mt-1.5 text-xs sm:text-sm">Live door list · Bookings · Revenue split.</p>
      <Link
        to={`/admin/night-sheet?date=${dateFilter || new Date().toISOString().slice(0, 10)}`}
        data-testid="print-night-sheet-btn"
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neon/60 text-[11px] uppercase tracking-widest font-bold text-white hover:bg-neon/10 transition-colors"
      >
        <Printer size={14} className="text-neon" /> Print Night Sheet
      </Link>

      {/* Auto-confirm status banner — stacks on mobile */}
      <div className="mt-4 sm:mt-3 rounded-2xl border border-neon/25 bg-neon/[0.03] px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[10px] uppercase tracking-widest">
          <span className="text-neon font-bold">Auto-Confirm ON</span>
          <span className="text-white/45 text-[10px] leading-relaxed flex-1 min-w-[180px]">
            Every paid booking is confirmed automatically — no manual step needed.
          </span>
          <div className="flex gap-2 shrink-0 w-full sm:w-auto">
            <button
              data-testid="sync-payments-btn"
              onClick={async () => {
                try {
                  const r = await api.post("/admin/reconcile-payments");
                  toast.success(`Sync done · ${r.data.flipped} confirmed`);
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="flex-1 sm:flex-initial rounded-full border border-neon/50 px-3 py-1.5 text-neon hover:bg-neon/10 transition"
            >
              Sync now
            </button>
            <button
              data-testid="telegram-test-btn"
              onClick={async () => {
                try {
                  const r = await api.post("/admin/telegram-test");
                  if (r.data.ok) {
                    toast.success("Test sent — check Taylor's Telegram");
                  } else {
                    toast.error(`Telegram: ${r.data.reason || `HTTP ${r.data.status_code}`}`);
                  }
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="flex-1 sm:flex-initial rounded-full border border-neon/50 px-3 py-1.5 text-neon hover:bg-neon/10 transition"
            >
              Test Telegram
            </button>
          </div>
        </div>
      </div>

      {/* Finance dashboard — Stripe-only money. Door cash is excluded per
          Taylor's spec (2026-08-22).

          Math:
            Drink cards (100% upfront online):
              → 30% Taylor, 70% Club (split calculator only).
            Table deposits (30% upfront online):
              → 100% Taylor. NEVER touches the split.
            Paid at Bar (the 70% at-venue balance on table bookings):
              → Manually ticked. Feeds `Total Revenue Generated` only.
                Never touches the split.

          Tiles:
            Lifetime Revenue                = drink cards + table deposits
                                              (all Stripe cash, never reset).
            Taylor's Commission             = 30% × drink cards
                                              + 100% × table deposits.
            Club's Lifetime 70% Share       = 70% × drink cards, permanent.
            Club's Share Since Settlement   = 70% × drink cards minus every
                                              recorded settlement. Floors at
                                              €0. Reduced by Record Settlement.
          */}
      <div className="mt-4 sm:mt-6 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <Mini
          label="Lifetime Revenue"
          value={stats ? `€${stats.lifetime_revenue_eur ?? 0}` : "—"}
          testid="staff-stat-lifetime"
        />
        <Mini
          label="Taylor's Commission (30% Cards + 100% Deposits)"
          value={stats ? `€${stats.commission_eur ?? 0}` : "—"}
          testid="staff-stat-commission"
        />
        <Mini
          label="Club's Lifetime 70% Share (Drink Cards)"
          value={stats ? `€${stats.club_lifetime_70_eur ?? stats.owed_to_club_eur ?? 0}` : "—"}
          testid="staff-stat-club-lifetime"
        />
        <div
          data-testid="staff-stat-owed-current"
          className="surface rounded-3xl p-3 md:p-4 relative"
        >
          <div className="text-[9px] sm:text-[10px] tracking-widest uppercase text-neon font-bold">
            Club&apos;s Share Since Last Settlement
          </div>
          <div className="mt-1 font-heading text-2xl md:text-3xl">
            {stats ? `€${stats.club_since_settlement_eur ?? stats.owed_to_club_current_eur ?? 0}` : "—"}
          </div>
          {stats && stats.settlements_paid_eur > 0 ? (
            <div className="mt-1 text-[9px] uppercase tracking-widest text-white/40">
              €{stats.settlements_paid_eur} paid · lifetime €{stats.club_lifetime_70_eur ?? stats.owed_to_club_eur}
            </div>
          ) : null}
          <button
            onClick={() => setSettleModalOpen(true)}
            data-testid="record-settlement-btn"
            className="mt-2 w-full rounded-full border border-neon/50 px-2 py-1 text-neon hover:bg-neon/10 transition text-[10px] uppercase tracking-widest font-bold"
          >
            Record Settlement
          </button>
        </div>
      </div>

      {/* Paid at Bar — the 70% door-cash balance on table bookings.
          Tick a row once cash is in the till. Feeds `Total Revenue
          Generated` ONLY — has no path into the share calculator. */}
      <AtBarSection api={api} stats={stats} />

      {/* Settlement History — visible only when at least one payout exists. */}
      {settlements.length > 0 ? (
        <section
          className="mt-4 sm:mt-6 surface rounded-3xl p-4 md:p-6"
          data-testid="settlement-history-section"
        >
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <h2 className="font-heading text-lg md:text-xl uppercase">Settlement History</h2>
            <div className="text-[10px] uppercase tracking-widest text-white/40">
              {settlements.length} payout{settlements.length === 1 ? "" : "s"} · €{stats?.settlements_paid_eur ?? 0} total
            </div>
          </div>
          <div className="grid gap-2 max-h-72 overflow-y-auto">
            {settlements.map((s) => (
              <div
                key={s.id}
                data-testid={`settlement-row-${s.id}`}
                className="rounded-2xl border border-white/10 bg-black/40 px-3 py-2 flex items-center justify-between gap-3 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-white font-bold">
                    €{Number(s.amount_eur).toFixed(2)}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45 truncate">
                    {new Date(s.created_at).toLocaleDateString(undefined, {
                      year: "numeric", month: "short", day: "2-digit",
                    })}
                    {" · "}
                    {new Date(s.created_at).toLocaleTimeString(undefined, {
                      hour: "2-digit", minute: "2-digit",
                    })}
                    {s.actor_email ? ` · ${s.actor_email}` : ""}
                  </div>
                  {s.note ? (
                    <div className="text-[11px] text-white/60 italic mt-0.5 truncate">
                      {s.note}
                    </div>
                  ) : null}
                </div>
                <button
                  onClick={() => undoSettlement(s.id)}
                  data-testid={`settlement-undo-${s.id}`}
                  className="text-white/35 hover:text-red-400 p-1 shrink-0"
                  aria-label="Undo settlement"
                  title="Undo (adds amount back to Current Balance)"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Record Settlement modal */}
      {settleModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="settlement-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setSettleModalOpen(false); }}
        >
          <div className="w-full max-w-sm surface rounded-3xl p-5 md:p-6 border border-neon/40">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading text-2xl uppercase">Record Settlement</h3>
              <button
                onClick={() => setSettleModalOpen(false)}
                data-testid="settlement-modal-close"
                className="text-white/40 hover:text-white p-1"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-[11px] uppercase tracking-widest text-white/45 mt-1">
              Paid €{stats?.owed_to_club_current_eur ?? 0} owed · reduces Current Balance only
            </p>
            <label className="block mt-4 text-[10px] uppercase tracking-widest text-neon font-bold">
              Amount Paid (EUR)
            </label>
            <div className="mt-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none">€</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                value={settleAmount}
                onChange={(e) => setSettleAmount(e.target.value)}
                autoFocus
                data-testid="settlement-amount-input"
                className="w-full pl-7 pr-3 py-2 rounded-xl bg-black/60 border border-white/15 focus:border-neon outline-none text-white text-lg font-heading"
                placeholder="0.00"
              />
            </div>
            <label className="block mt-3 text-[10px] uppercase tracking-widest text-white/50 font-bold">
              Note (optional)
            </label>
            <input
              type="text"
              value={settleNote}
              onChange={(e) => setSettleNote(e.target.value)}
              maxLength={120}
              data-testid="settlement-note-input"
              className="mt-1 w-full px-3 py-2 rounded-xl bg-black/60 border border-white/15 focus:border-neon outline-none text-white text-sm"
              placeholder="e.g. cash handoff Sat night"
            />
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setSettleModalOpen(false)}
                data-testid="settlement-modal-cancel"
                className="flex-1 rounded-full border border-white/20 py-2 text-white/70 hover:bg-white/5 text-xs uppercase tracking-widest font-bold"
              >
                Cancel
              </button>
              <button
                onClick={submitSettlement}
                disabled={settleSaving || !settleAmount}
                data-testid="settlement-modal-confirm"
                className="flex-1 rounded-full bg-neon text-black py-2 hover:bg-neon/90 disabled:opacity-50 text-xs uppercase tracking-widest font-bold"
              >
                {settleSaving ? "Saving…" : "Record Payment"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Ops counts — 2 cols on mobile, 4 on md+ */}
      <div className="mt-3 sm:mt-6 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <Mini label="Tonight pending" value={stats?.pending ?? "—"} testid="staff-stat-pending" />
        <Mini label="Confirmed" value={stats?.confirmed ?? "—"} testid="staff-stat-confirmed" />
        <Mini label="Tables" value={stats?.table_bookings ?? "—"} testid="staff-stat-tables" />
        <Mini label="Tickets" value={stats?.ticket_bookings ?? "—"} testid="staff-stat-tickets" />
      </div>

      {/* Calendar picker — click any day to filter bookings; pink dot on days with bookings */}
      <BookingsCalendar
        monthAnchor={monthAnchor}
        setMonthAnchor={setMonthAnchor}
        selectedDate={dateFilter}
        onSelectDate={setDateFilter}
        counts={monthCounts}
      />

      {/* Search (calendar handles date filtering now) */}
      <div className="mt-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="staff-search" placeholder="Search name, email, phone…"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 className="bg-[#0a0a0a] border border-white/15 rounded-full pl-9 pr-4 py-2 w-full text-sm" />
        </div>
        <button
          data-testid="staff-clear-date"
          onClick={() => setDateFilter("")}
          className="rounded-full border border-white/15 px-4 py-2 text-[11px] uppercase tracking-widest hover:border-neon whitespace-nowrap"
        >
          Show all dates
        </button>
      </div>

      <div className="mt-6 border border-white/10">
        {filtered.length === 0 && (
          <div className="p-6 text-white/40 text-sm">No bookings for this date.</div>
        )}
        {filtered.map((b) => (
          <div key={b.id} data-testid={`staff-booking-${b.id}`} onClick={() => setDetail(b)}
               className="grid grid-cols-12 gap-3 items-center px-4 py-4 border-b border-white/10 last:border-b-0 hover:bg-white/[0.04] cursor-pointer">
            <div className="col-span-12 md:col-span-3">
              <div className="font-medium">{b.name}</div>
              <div className="text-xs text-white/50">{b.whatsapp}</div>
              <div className="text-xs text-white/40">{b.email}</div>
            </div>
            <div className="col-span-6 md:col-span-3">
              <div className="text-[10px] uppercase tracking-widest text-neon">
                {b.type === "table" ? "Table" : b.type === "barorder" ? "VIP Pre-Order" : "Ticket"}
              </div>
              <div className="text-sm">{b.type === "table" ? b.table_name : b.event_title || "—"}</div>
              <OrderSummary b={b} testid={`staff-booking-order-${b.id}`} />
            </div>
            <div className="col-span-6 md:col-span-2 text-sm">
              <div>{b.date}</div>
              <div className="text-xs text-white/50">
                {b.type === "table" ? `${b.arrival_time} · ${b.party_size}p` : b.type === "ticket" ? `${b.quantity} tix` : ""}
              </div>
            </div>
            <div className="col-span-12 md:col-span-4 flex gap-2 flex-wrap md:justify-end" onClick={(e) => e.stopPropagation()}>
              {STATUSES.map((s) => (
                <button key={s} data-testid={`staff-set-${s}-${b.id}`}
                        onClick={() => updateStatus(b.id, s)}
                        className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition ${
                          b.status === s
                            ? "bg-neon text-black border-neon"
                            : "border-white/15 text-white/70 hover:border-neon hover:text-neon"
                        }`}>
                  {s === "checked_in" ? "Check in" : s}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {detail && <BookingDetailModal booking={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

/* ───────────────────────────── BOOKINGS (all, with delete) ───────────────────────────── */
function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState({ type: "", status: "" });
  // Two-step delete guard: first click sets this to the booking id, revealing
  // an inline "Confirm?" button in the row. A second click within 5 seconds
  // actually deletes; anything else clears it. Prevents accidental one-tap
  // deletion of a real customer's booking.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [detail, setDetail] = useState(null);
  const missing = bookings.filter(isOrderMissing);

  const load = useCallback(async () => {
    try {
      const b = await api.get("/bookings", {
        params: {
          ...(filter.type && { type: filter.type }),
          ...(filter.status && { status_f: filter.status }),
        },
      });
      setBookings(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Auto-cancel the pending delete after 5s so the confirm button doesn't
  // linger on-screen forever if the manager gets distracted.
  useEffect(() => {
    if (!pendingDeleteId) return;
    const timer = setTimeout(() => setPendingDeleteId(null), 5000);
    return () => clearTimeout(timer);
  }, [pendingDeleteId]);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/bookings/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const requestDelete = (id) => {
    setPendingDeleteId(id);
  };
  const cancelDelete = () => setPendingDeleteId(null);
  const confirmDelete = async (id) => {
    setPendingDeleteId(null);
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      // Optimistic remove — snappier than waiting for the reload round-trip.
      setBookings((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
      load();  // fall back to server truth on error
    }
  };

  return (
    <div data-testid="staff-bookings-tab">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl uppercase">All bookings</h1>
          <p className="text-white/60 mt-1.5 text-xs sm:text-sm">Manage every table, ticket and bar order.</p>
        </div>
        <div className="flex gap-2 sm:gap-3 flex-wrap">
          <select data-testid="filter-type" value={filter.type}
                  onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}
                  className="flex-1 sm:flex-initial rounded-full bg-black/40 border border-white/10 px-3 py-2 text-xs uppercase tracking-widest">
            <option value="">All types</option>
            <option value="table">Tables</option>
            <option value="ticket">Tickets</option>
            <option value="barorder">VIP Pre-Order</option>
          </select>
          <select data-testid="filter-status" value={filter.status}
                  onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
                  className="flex-1 sm:flex-initial rounded-full bg-black/40 border border-white/10 px-3 py-2 text-xs uppercase tracking-widest">
            <option value="">All status</option>
            {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
      </div>

      {missing.length > 0 && (
        <div data-testid="missing-orders-notice" className="mt-6 rounded-3xl border border-orange-400/40 bg-orange-400/5 p-4">
          <div className="text-[11px] uppercase tracking-widest text-orange-300 font-bold">
            {missing.length} booking{missing.length > 1 ? "s" : ""} with no order saved
          </div>
          <ul className="mt-2 space-y-1">
            {missing.map((b) => (
              <li key={b.id}>
                <button type="button" onClick={() => setDetail(b)} data-testid={`missing-order-${b.id}`}
                        className="text-xs text-white/80 hover:text-neon text-left">
                  {b.name} · {b.date} · {b.type === "barorder" ? "VIP Pre-Order" : "Table"} · €{b.total_price_eur ?? b.price_eur ?? 0}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 surface rounded-4xl overflow-hidden">
        {bookings.length === 0 && (<div className="p-6 text-white/40 text-sm">No bookings match the current filter.</div>)}
        {bookings.map((b) => (
          <div key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => setDetail(b)}
               className="grid grid-cols-12 items-center gap-4 px-4 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.04] cursor-pointer">
            <div className="col-span-12 md:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-neon font-bold">
                {b.type === "table" ? "Table" : b.type === "barorder" ? "VIP Pre-Order" : "Ticket"}
              </div>
              <div className="text-sm">{b.type === "table" ? b.table_name : b.event_title || "—"}</div>
              <OrderSummary b={b} testid={`booking-order-${b.id}`} />
            </div>
            <div className="col-span-6 md:col-span-3">
              <div className="text-sm font-medium">{b.name}</div>
              <div className="text-xs text-white/50">{b.email}</div>
              <div className="text-xs text-white/50">{b.whatsapp}</div>
            </div>
            <div className="col-span-6 md:col-span-2 text-sm">
              <div>{b.date}</div>
              <div className="text-xs text-white/50">
                {b.type === "table" ? `${b.arrival_time} · ${b.party_size}p` : b.type === "ticket" ? `${b.quantity} tix` : ""}
              </div>
            </div>
            <div className="col-span-6 md:col-span-2">
              <StatusBadge status={b.status} />
              {(b.type === "table" || b.type === "barorder") && (
                <>
                  <div className="text-[10px] text-white/50 mt-1">Total €{b.total_price_eur ?? b.price_eur}</div>
                  <div className="text-[10px] text-neon font-bold">Paid €{b.deposit_eur ?? "—"}</div>
                  <div className="text-[10px] text-gold font-bold">Balance €{b.balance_eur ?? "—"}</div>
                </>
              )}
            </div>
            <div className="col-span-6 md:col-span-3 flex flex-wrap gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
              <select data-testid={`status-select-${b.id}`} value={b.status}
                      onChange={(e) => updateStatus(b.id, e.target.value)}
                      className="rounded-full bg-black/40 border border-white/10 px-2 py-1 text-[10px] uppercase tracking-widest">
                {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
              {pendingDeleteId === b.id ? (
                <div className="flex items-center gap-1" data-testid={`confirm-delete-${b.id}`}>
                  <button
                    onClick={() => confirmDelete(b.id)}
                    data-testid={`confirm-delete-btn-${b.id}`}
                    className="rounded-full bg-red-500 hover:bg-red-600 text-white px-2 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors"
                  >
                    Confirm?
                  </button>
                  <button
                    onClick={cancelDelete}
                    data-testid={`cancel-delete-${b.id}`}
                    className="text-white/40 hover:text-white p-1"
                    aria-label="Cancel delete"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => requestDelete(b.id)}
                  data-testid={`delete-${b.id}`}
                  className="text-white/40 hover:text-red-400 p-1"
                  aria-label="Delete booking"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {detail && <BookingDetailModal booking={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

/* ───────────────────────────── EVENTS ───────────────────────────── */
function EventsTab() {
  const [weekly, setWeekly] = useState([]);
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({
    title: "", dj: "", date: "", description: "", is_featured: false,
  });

  const load = useCallback(async () => {
    try {
      const [w, e] = await Promise.all([
        api.get("/weekly-lineup"),
        api.get("/events"),
      ]);
      setWeekly(w.data);
      setEvents(e.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveWeeklyDay = async (day, patch) => {
    try {
      const r = await api.patch(`/weekly-lineup/${day}`, patch);
      setWeekly((prev) => prev.map((w) => (w.day === day ? r.data : w)));
      toast.success(`${day.toUpperCase()} updated`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post("/events", newEvent);
      toast.success("Event created");
      setNewEvent({ title: "", dj: "", date: "", description: "", is_featured: false });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="staff-events-tab">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl uppercase">Events</h1>
        <p className="text-white/60 mt-1.5 text-xs sm:text-sm">
          Edit the weekly line-up shown on the website · add one-off events below.
        </p>
      </div>

      {/* Weekly line-up — editable, live on the website */}
      <div className="mt-6 sm:mt-8">
        <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold flex items-center gap-2">
          <Calendar size={12} /> Weekly Line-Up (live on the website)
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {weekly.map((w) => (
            <WeeklyCard key={w.day} card={w} onSave={saveWeeklyDay} />
          ))}
        </div>
      </div>

      {/* Special events — full CRUD */}
      <div className="mt-10 text-[10px] uppercase tracking-[0.32em] text-neon font-bold flex items-center gap-2">
        <Plus size={12} /> Add a One-Off Event
      </div>
      <form onSubmit={createEvent} data-testid="event-create-form"
            className="mt-3 surface rounded-4xl p-4 sm:p-6 grid sm:grid-cols-2 gap-3 sm:gap-4">
        <Input testid="event-title" label="Title" required value={newEvent.title}
               onChange={(v) => setNewEvent((e) => ({ ...e, title: v }))} />
        <Input testid="event-dj" label="DJ" required value={newEvent.dj}
               onChange={(v) => setNewEvent((e) => ({ ...e, dj: v }))} />
        <Input testid="event-date" label="Date" type="date" required value={newEvent.date}
               onChange={(v) => setNewEvent((e) => ({ ...e, date: v }))} />
        <label className="flex items-center gap-3 mt-6">
          <input type="checkbox" data-testid="event-featured" checked={newEvent.is_featured}
                 onChange={(e) => setNewEvent((s) => ({ ...s, is_featured: e.target.checked }))}
                 className="accent-pink-500 w-5 h-5" />
          <span className="text-xs uppercase tracking-widest text-white/70">Featured</span>
        </label>
        <div className="sm:col-span-2">
          <Input testid="event-description" label="Description" value={newEvent.description}
                 onChange={(v) => setNewEvent((e) => ({ ...e, description: v }))} />
        </div>
        <button type="submit" data-testid="event-create-btn" className="btn-pink sm:col-span-2">
          <Plus size={14} /> Create event
        </button>
      </form>

      {events.length > 0 && (
        <>
          <div className="mt-8 text-[10px] uppercase tracking-[0.32em] text-neon font-bold">
            Upcoming One-Off Events
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3 sm:gap-4">
            {events.map((e, idx) => (
              <div key={e.id} data-testid={`staff-event-${idx}`}
                   className="surface rounded-4xl p-4 sm:p-5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">
                    {new Date(e.date).toLocaleDateString("en-GB")}
                  </div>
                  <div className="font-heading text-xl sm:text-2xl mt-1 uppercase break-words">{e.title}</div>
                  <div className="text-white/70 text-sm mt-1">{e.dj}</div>
                  {e.description && <div className="text-white/50 text-xs mt-2">{e.description}</div>}
                  {e.is_featured && (
                    <span className="text-[10px] uppercase tracking-widest text-black px-2 py-0.5 mt-2 inline-block rounded-full font-bold"
                          style={{ background: "linear-gradient(135deg,#FFC857,#FF8B42)" }}>
                      Featured
                    </span>
                  )}
                </div>
                <button onClick={() => deleteEvent(e.id)} data-testid={`event-delete-${idx}`}
                        className="text-white/40 hover:text-red-400 shrink-0" aria-label="Delete">
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* Editable weekly-lineup card. Local dirty state, saves via PATCH on blur. */
function WeeklyCard({ card, onSave }) {
  const [title, setTitle] = useState(card.title);
  const [subtitle, setSubtitle] = useState(card.subtitle);
  const [featured, setFeatured] = useState(!!card.is_featured);
  const dirty =
    title !== card.title || subtitle !== card.subtitle || featured !== !!card.is_featured;

  const commit = () => {
    if (!dirty) return;
    onSave(card.day, { title, subtitle, is_featured: featured });
  };

  return (
    <div
      data-testid={`staff-weekly-card-${card.day}`}
      className={
        "relative rounded-2xl p-4 border " +
        (featured
          ? "border-neon shadow-[0_0_20px_rgba(255,27,141,0.35)]"
          : "border-white/10")
      }
      style={{
        background:
          "linear-gradient(160deg, rgba(255,27,141,0.18) 0%, rgba(10,0,20,0.9) 100%)",
      }}
    >
      <div className="text-[10px] uppercase tracking-widest text-white/60">
        {card.day.toUpperCase()}
      </div>
      <input
        data-testid={`weekly-title-${card.day}`}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={commit}
        className="mt-1 w-full bg-transparent font-heading text-lg sm:text-xl leading-tight focus:outline-none focus:border-b focus:border-neon"
        placeholder="Title"
      />
      <textarea
        data-testid={`weekly-subtitle-${card.day}`}
        value={subtitle}
        onChange={(e) => setSubtitle(e.target.value)}
        onBlur={commit}
        rows={2}
        className="mt-2 w-full bg-black/30 rounded-lg px-2 py-1.5 text-xs italic text-white/80 focus:outline-none focus:ring-1 focus:ring-neon resize-none"
        placeholder="Tagline"
      />
      <label className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-widest">
        <input
          type="checkbox"
          data-testid={`weekly-featured-${card.day}`}
          checked={featured}
          onChange={(e) => { setFeatured(e.target.checked); onSave(card.day, { is_featured: e.target.checked }); }}
          className="accent-pink-500 w-4 h-4"
        />
        <span className="text-white/70">Featured</span>
        {dirty && <span className="ml-auto text-neon">● Unsaved</span>}
      </label>
    </div>
  );
}

/* ───────────────────────────── 6+6 LOYALTY ───────────────────────────── */
function LoyaltyTab() {
  const [data, setData] = useState(null);
  const [abandoned, setAbandoned] = useState([]);
  // Two-step delete guard — same pattern as BookingsTab. First click sets the
  // card id, revealing an inline "Confirm?" button; second click within 5s
  // actually deletes; any other click clears it.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showAbandoned, setShowAbandoned] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        api.get("/loyalty/cards"),
        api.get("/admin/abandoned-cards"),
      ]);
      setData(r.data);
      setAbandoned(a.data?.abandoned || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Immediate sync — poll every 15s while the tab is visible so a new
  // paid card popped up by Stripe's webhook shows up without the manager
  // having to reload the page. Cheap: /loyalty/cards is a single indexed
  // find, and the poll pauses if the browser tab is backgrounded.
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 15000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (!pendingDeleteId) return;
    const timer = setTimeout(() => setPendingDeleteId(null), 5000);
    return () => clearTimeout(timer);
  }, [pendingDeleteId]);

  const requestDelete = (id) => setPendingDeleteId(id);
  const cancelDelete = () => setPendingDeleteId(null);
  const confirmDelete = async (id) => {
    setPendingDeleteId(null);
    try {
      await api.delete(`/loyalty/cards/${id}`);
      toast.success("Card deleted");
      // Optimistic — snappier than refetch.
      setData((prev) => {
        if (!prev) return prev;
        const nextCards = (prev.cards || []).filter((x) => x.id !== id);
        const nextStats = { ...(prev.stats || {}) };
        nextStats.total_sales = Math.max(0, (nextStats.total_sales || 0) - 1);
        return { ...prev, cards: nextCards, stats: nextStats };
      });
      // Refresh to reconcile stats (active/completed/revenue) with server truth.
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const deleteAbandoned = async (id) => {
    try {
      await api.delete(`/admin/abandoned-cards/${id}`);
      toast.success("Abandoned entry removed");
      setAbandoned((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const stats = data?.stats;
  const cards = data?.cards || [];

  return (
    <div data-testid="staff-loyalty-tab">
      <header className="surface rounded-4xl p-5 md:p-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-5xl">6+6 Cards</h1>
          <p className="text-white/60 mt-2 text-sm">
            Cards are auto-active on purchase. Tap a slot on the guest&apos;s phone, enter the staff PIN to mark it REDEEMED.
          </p>
        </div>
        <div className="hidden md:block text-right text-xs uppercase tracking-widest text-white/40">
          Staff PIN to redeem: <span className="text-neon font-bold">1269</span>
        </div>
      </header>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Mini label="Cards sold" value={stats?.total_sales ?? "—"} testid="loyalty-stat-sales" />
        <Mini label="Revenue" value={`€${stats?.total_revenue_eur ?? 0}`} testid="loyalty-stat-revenue" />
        <Mini label="Items redeemed" value={stats?.total_items_redeemed ?? 0} testid="loyalty-stat-redeemed" />
        <Mini label="Active" value={stats?.active_cards ?? 0} testid="loyalty-stat-active" />
      </div>

      <section className="mt-6 surface rounded-4xl p-5 md:p-8">
        <h2 className="font-heading text-2xl uppercase">All cards</h2>
        <div className="mt-4 grid gap-3">
          {cards.length === 0 && (<div className="text-white/50 text-sm">No cards sold yet.</div>)}
          {cards.map((c) => {
            const drinksUsed = (c.drinks || []).filter((s) => s.used).length;
            const shotsUsed = (c.shots || []).filter((s) => s.used).length;
            return (
              <div key={c.id} data-testid={`loyalty-card-row-${c.id}`}
                   className="rounded-2xl border border-white/10 bg-black/40 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold">{c.name}</div>
                  <div className="text-xs text-white/55 truncate">{c.email} · {c.phone}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/40">Drinks</div>
                    <div className="text-neon font-bold">{drinksUsed}/6</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-widest text-white/40">Shots</div>
                    <div className="text-neon font-bold">{shotsUsed}/6</div>
                  </div>
                  <span className={
                      "px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border " +
                      (c.status === "active"
                        ? "border-emerald-400/40 text-emerald-300"
                        : c.status === "done"
                        ? "border-white/30 text-white/60"
                        : "border-yellow-400/40 text-yellow-300")}>
                    {c.status}
                  </span>
                  <a href={`/card/${c.qr_token}`} target="_blank" rel="noreferrer"
                     className="text-neon text-xs underline" data-testid={`loyalty-card-open-${c.id}`}>
                    open
                  </a>
                  {pendingDeleteId === c.id ? (
                    <div className="flex items-center gap-1" data-testid={`loyalty-confirm-delete-${c.id}`}>
                      <button
                        onClick={() => confirmDelete(c.id)}
                        data-testid={`loyalty-confirm-delete-btn-${c.id}`}
                        className="rounded-full bg-red-500 hover:bg-red-600 text-white px-2 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors"
                      >
                        Confirm?
                      </button>
                      <button
                        onClick={cancelDelete}
                        data-testid={`loyalty-cancel-delete-${c.id}`}
                        className="text-white/40 hover:text-white p-1"
                        aria-label="Cancel delete"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => requestDelete(c.id)}
                      data-testid={`loyalty-delete-${c.id}`}
                      className="text-white/40 hover:text-red-400 p-1"
                      aria-label="Delete card"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Abandoned Cards — hidden by default, expandable. Rows here are
          intent-only (customer clicked "Buy" but never completed Stripe).
          Kept so Taylor can follow up manually; never touches sales stats. */}
      <section className="mt-6" data-testid="abandoned-cards-section">
        <button
          onClick={() => setShowAbandoned((v) => !v)}
          data-testid="abandoned-cards-toggle"
          className="w-full surface rounded-4xl p-4 md:p-5 flex items-center justify-between hover:bg-white/[0.02] transition"
        >
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/40 font-bold">
              Abandoned Carts
            </div>
            <div className="mt-1 font-heading text-lg md:text-xl">
              {abandoned.length}{" "}
              <span className="text-white/50 text-sm font-body normal-case">
                {abandoned.length === 1 ? "customer" : "customers"} started but didn&apos;t pay
              </span>
            </div>
          </div>
          <div className="text-neon text-xs uppercase tracking-widest font-bold">
            {showAbandoned ? "Hide" : "View"} →
          </div>
        </button>

        {showAbandoned && abandoned.length > 0 ? (
          <div className="mt-3 grid gap-2 max-h-96 overflow-y-auto">
            {abandoned.map((c) => (
              <div
                key={c.id}
                data-testid={`abandoned-row-${c.id}`}
                className="rounded-2xl border border-white/10 bg-black/40 px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-bold flex items-center gap-2">
                    {c.name}
                    {c._legacy ? (
                      <span className="text-[9px] uppercase tracking-widest text-yellow-300/70 border border-yellow-400/30 rounded-full px-1.5 py-0.5">
                        legacy
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-white/55 truncate">
                    {c.email} · {c.phone}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href={`mailto:${c.email}?subject=Your%20Soho69%206%2B6%20drink%20pass&body=Hi%20${encodeURIComponent(c.name)}%2C%20we%20saw%20you%20started%20a%20purchase%20-%20need%20a%20hand%20completing%20it%3F`}
                    className="text-neon text-xs underline"
                    data-testid={`abandoned-email-${c.id}`}
                  >
                    email
                  </a>
                  <button
                    onClick={() => deleteAbandoned(c.id)}
                    data-testid={`abandoned-delete-${c.id}`}
                    className="text-white/40 hover:text-red-400 p-1"
                    aria-label="Delete abandoned entry"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {showAbandoned && abandoned.length === 0 ? (
          <div className="mt-3 text-white/40 text-sm italic px-2">
            No abandoned purchases — every customer who clicked Buy paid.
          </div>
        ) : null}
      </section>
    </div>
  );
}

/* ───────────────────────────── shared ───────────────────────────── */
function TopTab({ icon: Icon, active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={
              "flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.22em] transition rounded-full whitespace-nowrap " +
              (active
                ? "text-white shadow-[0_0_16px_rgba(255,27,141,0.45)]"
                : "text-white/70 hover:text-white hover:bg-white/5")
            }
            style={active ? { background: "linear-gradient(135deg,#FF1B8D,#FF6BC6)" } : {}}>
      <Icon size={13} /> {children}
    </button>
  );
}

/* Full-month calendar picker — Taylor taps any day to filter bookings for
   that date. Days that already have bookings get a bright neon dot beneath
   the number so he can see busy nights at a glance. */
function BookingsCalendar({ monthAnchor, setMonthAnchor, selectedDate, onSelectDate, counts }) {
  const year = monthAnchor.getFullYear();
  const month = monthAnchor.getMonth();
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Monday-first week (0 = Mon .. 6 = Sun). Sunday in JS is 0, so shift.
  const startOffset = (first.getDay() + 6) % 7;
  const today = new Date().toISOString().slice(0, 10);

  const cells = [];
  for (let i = 0; i < startOffset; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, iso });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = monthAnchor.toLocaleString("en", { month: "long", year: "numeric" });
  const prev = () => setMonthAnchor(new Date(year, month - 1, 1));
  const next = () => setMonthAnchor(new Date(year, month + 1, 1));
  const goToday = () => {
    const now = new Date();
    setMonthAnchor(new Date(now.getFullYear(), now.getMonth(), 1));
    onSelectDate(now.toISOString().slice(0, 10));
  };

  return (
    <div data-testid="staff-calendar" className="mt-6 sm:mt-8 rounded-3xl border border-white/10 bg-black/50 p-3 sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <button
          data-testid="cal-prev"
          onClick={prev}
          aria-label="Previous month"
          className="w-9 h-9 rounded-full flex items-center justify-center border border-white/15 text-white/80 hover:border-neon hover:text-neon transition"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1 text-center">
          <div className="font-heading uppercase text-lg sm:text-xl tracking-widest">
            {monthLabel}
          </div>
          <button
            onClick={goToday}
            className="mt-0.5 text-[10px] uppercase tracking-widest text-neon/80 hover:text-neon"
            data-testid="cal-today"
          >
            Jump to today
          </button>
        </div>
        <button
          data-testid="cal-next"
          onClick={next}
          aria-label="Next month"
          className="w-9 h-9 rounded-full flex items-center justify-center border border-white/15 text-white/80 hover:border-neon hover:text-neon transition"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-white/40 text-center">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1 sm:gap-2">
        {cells.map((c, i) => {
          if (!c) return <div key={`e${i}`} className="aspect-square" />;
          const isSelected = c.iso === selectedDate;
          const isToday = c.iso === today;
          const count = counts[c.iso] || 0;
          return (
            <button
              key={c.iso}
              data-testid={`cal-day-${c.iso}`}
              onClick={() => onSelectDate(c.iso)}
              aria-pressed={isSelected}
              className={
                "aspect-square rounded-lg sm:rounded-xl flex flex-col items-center justify-center text-sm sm:text-base transition border " +
                (isSelected
                  ? "bg-neon text-black border-neon font-bold shadow-[0_0_20px_rgba(255,27,141,0.55)]"
                  : isToday
                  ? "border-neon/60 text-white bg-neon/10 hover:bg-neon/20"
                  : count > 0
                  ? "border-white/15 text-white bg-white/[0.03] hover:border-neon hover:text-neon"
                  : "border-transparent text-white/40 hover:bg-white/5 hover:text-white")
              }
            >
              <span className="leading-none">{c.day}</span>
              {count > 0 && (
                <span
                  className={
                    "mt-1 h-1.5 w-1.5 rounded-full " +
                    (isSelected ? "bg-black/70" : "bg-neon")
                  }
                  aria-label={`${count} bookings`}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex items-center gap-4 text-[10px] uppercase tracking-widest text-white/50 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-neon" /> Has bookings
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full bg-neon" /> Selected
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full border border-neon/60 bg-neon/10" /> Today
        </span>
      </div>
    </div>
  );
}


function Mini({ label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-white/10 p-3 sm:p-4 md:p-5 bg-[#0a0a0a] rounded-xl">
      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] text-neon leading-tight">{label}</div>
      <div className="font-heading text-2xl sm:text-3xl tracking-wide mt-1 truncate">{value}</div>
    </div>
  );
}

function Input({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{label}</span>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} {...rest}
             className="mt-2 w-full rounded-2xl bg-black/40 border border-white/10 px-4 py-2.5 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon" />
    </label>
  );
}

function StatusBadge({ status }) {
  const { c, Icon } = STATUSES_DISPLAY[status] || STATUSES_DISPLAY.pending;
  return (
    <span className={"inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-full " + c}>
      <Icon size={10} /> {status}
    </span>
  );
}


/**
 * Paid at Bar section. Read-only running total of every paid table
 * booking's 70 % at-venue balance.
 *
 * Once the deposit clears we treat the balance as expected bar revenue —
 * no manual "mark received" step. The sum is fed to
 * `total_revenue_generated_eur` only; the share calculator never sees it.
 */
function AtBarSection({ api, stats }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/at-bar");
      setRows(r.data?.at_bar || []);
    } catch (e) {
      toast.error("Failed to load Paid at Bar rows");
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="paid-at-bar-section" className="mt-6 sm:mt-8">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">
            Paid at Bar
          </div>
          <div className="font-heading text-xl md:text-2xl uppercase mt-1">
            Door-Cash Balances
          </div>
          <p className="text-white/40 text-[11px] md:text-xs mt-1 max-w-md">
            Auto-tallied from every paid deposit · read-only · feeds Total
            Revenue Generated only.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Mini
            label="At-Bar Expected"
            value={stats ? `€${stats.at_bar_expected_eur ?? 0}` : "—"}
            testid="staff-stat-at-bar-expected"
          />
          <Mini
            label="Total Revenue Generated (Stripe + Bar)"
            value={stats ? `€${stats.total_revenue_generated_eur ?? 0}` : "—"}
            testid="staff-stat-total-revenue-generated"
          />
        </div>
      </div>

      <div className="mt-3 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-white/40 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-white/40 text-sm" data-testid="at-bar-empty">
            No paid table bookings yet.
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {rows.map((r) => (
              <li
                key={r.id}
                data-testid={`at-bar-row-${r.id}`}
                className="p-3 md:p-4 flex items-center justify-between gap-3 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-base md:text-lg truncate">
                    {r.name || "Guest"}
                    {r.package_name ? (
                      <span className="text-white/40 text-xs md:text-sm ml-2">
                        · {r.package_name}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-white/50 mt-0.5">
                    {r.date}
                    {r.arrival_time ? ` · ${r.arrival_time}` : ""} · deposit €{r.deposit_eur} / total €{r.total_eur}
                  </div>
                </div>
                <div className="font-heading text-lg md:text-2xl text-neon">
                  €{r.balance_eur}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
